import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, LoaderIcon, TruckIcon } from 'lucide-react';
import { builderAssets, builderPrices, builderSteps, completeProtection, essentialProtection, lensOptions, remainingLenses } from '../../data/builder';
import { BuilderApi } from '../../hooks/useBuilder';
import { protectionLabel } from '../../utils/builderCart';
import { money } from '../QuantityStepper';
import { RxMark } from '../PrescriptionBadge';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
import { UspsBadge } from '../UspsBadge';
import { BuilderOptionCard } from './BuilderOptionCard';
import { X1LensImage } from './BuilderVisual';
import { NotConnected } from './BuilderSummary';
interface BuilderStepPanelProps {
  builder: BuilderApi;
}
type AddOnStep = 'lensSystem' | 'case' | 'care' | 'rx';
const EASE = [0.23, 1, 0.32, 1] as const;
const titleClass = 'font-display text-3xl font-extrabold uppercase leading-none text-offwhite';
function Thumb({
  src,
  alt,
  filter
}: {
  src: string;
  alt: string;
  filter?: string;
}) {
  return <span className="block h-16 w-16 overflow-hidden border border-white/10 bg-ink-950 sm:h-20 sm:w-20" data-id="element-94" data-mp="fo10bcdc5ris">
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" style={filter ? {
      filter
    } : undefined} data-id="element-95" data-mp="ap7jb9yubidv" />
    </span>;
}

/** "Add" / "No thanks" for an optional step. Both move the build forward. */
function Decision({
  builder,
  kind,
  selected,
  included,
  addLabel,
  declineLabel,
  price
}: {
  builder: BuilderApi;
  kind: AddOnStep;
  selected: boolean;
  included: boolean;
  addLabel: string;
  declineLabel: string;
  price: number;
}) {
  const primary = 'inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite';
  const secondary = 'inline-flex items-center justify-center px-4 py-3.5 font-display text-base font-bold uppercase leading-none tracking-wide text-steel-200 border border-white/20 transition-[border-color,color] duration-150 ease-grit hover:border-white/40 hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500';
  if (included) {
    return <div className="mt-5 flex" data-id="element-96" data-mp="1rhunidb0wa0o">
        <button type="button" onClick={builder.nextStep} className={primary} data-id="element-97" data-mp="1riw75663u4u0">
          Included · Next
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-98" data-mp="1djd0yiwut3kc" />
        </button>
      </div>;
  }
  return <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-99" data-mp="1hl83fqqitmzp">
      {selected ? <>
          <button type="button" onClick={builder.nextStep} className={primary} data-id="element-100" data-mp="37tgo3385o1ak">
            <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-101" data-mp="3bwmtk8ng1ofz" />
            Added · Next
          </button>
          <button type="button" onClick={() => builder.decide(kind, false)} className={secondary} data-id="element-102" data-mp="107qa3y71v3o3">
            Remove
          </button>
        </> : <>
          <button type="button" onClick={() => builder.decide(kind, true)} className={primary} data-id="element-103" data-mp="1a176n9ykmwvg">
            {addLabel} · +{money(price)}
          </button>
          <button type="button" onClick={() => builder.decide(kind, false)} className={secondary} data-id="element-104" data-mp="1jxk2addofksu">
            {declineLabel}
          </button>
        </>}
    </div>;
}
export function BuilderStepPanel({
  builder
}: BuilderStepPanelProps) {
  const reduce = useReducedMotion();
  const {
    state,
    step,
    lines,
    total
  } = builder;
  const index = builderSteps.findIndex(item => item.id === step);
  const primaryLens = lensOptions.find(lens => lens.id === state.lens);
  return <div className="border border-white/10 bg-ink-900" data-id="element-105" data-mp="3pqs3ttmmrhwc">
      <AnimatePresence mode="wait" initial={false} data-id="element-106" data-mp="3psbijfsn1kw0">
        <motion.div key={step} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 12
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -12
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="p-4 sm:p-6" data-id="element-107" data-mp="3s5p3d1yclpln">
          {step === 'lens' && <>
              <h3 className={titleClass} data-id="element-108" data-mp="9xpxll55gat3">Choose your lens</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-109" data-mp="38ihcw5rxktr7">
                {state.complete ? 'Your Complete System includes all three. Pick the lens fitted on the frame.' : 'Which lens fits your work? Your X1 includes one lens of your choice.'}
              </p>
              <div role="radiogroup" aria-label="Primary lens" className="mt-5 grid grid-cols-3 gap-2 sm:gap-3" data-id="element-110" data-mp="3n7dsqp08bb2d">
                {lensOptions.map(lens => {
              const selected = state.lens === lens.id;
              return <button key={lens.id} type="button" role="radio" aria-checked={selected} onClick={() => builder.chooseLens(lens.id)} className={`group relative flex flex-col border-2 text-left transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${selected ? 'border-grit-500 bg-grit-500/[0.07]' : 'border-white/12 bg-ink-950 hover:border-white/30'}`} data-id="element-111" data-mp="2unosasx6ondr">
                      <X1LensImage lensId={lens.id} data-id="element-112" data-mp="1uh1phbk6ub9c" />
                      <span className="flex items-center gap-1.5 px-2 pt-2 sm:px-3" data-id="element-113" data-mp="1ufib5qi3eaa7">
                        <span aria-hidden="true" className="h-3 w-3 shrink-0 border border-white/30" style={{
                    backgroundColor: lens.swatch
                  }} data-id="element-114" data-mp="1pgnilwubp7cw" />
                        <span className="font-display text-lg font-extrabold uppercase leading-none text-offwhite sm:text-xl" data-id="element-115" data-mp="15wglh0ri81dt">
                          {lens.name}
                        </span>
                      </span>
                      <span className="px-2 pb-2.5 pt-1 text-[11px] leading-snug text-steel-400 sm:px-3 sm:text-[13px]" data-id="element-116" data-mp="wxlevnkjsp9z">
                        {lens.blurb}
                      </span>
                      {selected && <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center bg-grit-500 text-ink-950" data-id="element-117" data-mp="1plqxo4s1k476">
                          <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-118" data-mp="1fve45n2910ow" />
                        </span>}
                    </button>;
            })}
              </div>
              <p className="mt-4 text-[13px] text-steel-400" data-id="element-119" data-mp="28j1nyzvp3nah">Tap each lens to preview it on your X1.</p>
              <div className="mt-3 flex" data-id="element-120" data-mp="2dbbkwzv89nm9">
                <button type="button" onClick={builder.nextStep} className="inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-121" data-mp="2mpbzto8fdk84">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-122" data-mp="23skb2za46cwo" />
                  I want {primaryLens?.name}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-123" data-mp="2dp6rnbveqhsw" />
                </button>
              </div>
            </>}

          {step === 'lensSystem' && <>
              {state.fullLens || state.complete ? <>
                  <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-grit-500" data-id="element-124" data-mp="3p5n91aqiv8ie">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-125" data-mp="1ig3gpx818gyw" />
                    {state.complete ? 'Included in Complete' : `Full Lens System added · +${money(builderPrices.fullLensSystem)}`}
                  </p>
                  <h3 className={`${titleClass} mt-2`} data-id="element-126" data-mp="1rep0fvp8q219">
                    All 3 lenses. <span className="text-grit-500" data-id="element-127" data-mp="1w78eetruaz6l">One X1.</span>
                  </h3>
                  <p className="mt-2 text-[15px] text-steel-300" data-id="element-128" data-mp="216kzpa32by72">
                    {remainingLenses(state.lens).map(lens => lens.name).join(' + ')}{' '}
                    join your {primaryLens?.name} lens. Swap lenses to match the job.
                  </p>
                </> : <>
                  <h3 className={titleClass} data-id="element-129" data-mp="fsq8hugj5gy8">Want all 3 lenses?</h3>
                  <p className="mt-2 text-[15px] text-steel-300" data-id="element-130" data-mp="fu9t1x97ye5t">
                    Add the remaining two interchangeable lenses. Swap on the job instead of buying another pair.
                  </p>
                </>}
              {/* Real studio shot of all three lenses, labelled against the customer's pick */}
              <figure className={`mt-4 overflow-hidden border-2 bg-[#0e0e0f] transition-colors duration-200 ease-out ${state.fullLens || state.complete ? 'border-grit-500' : 'border-white/10'}`} data-id="element-131" data-mp="3ih3czan5689">
                <div className="relative aspect-[5/2] overflow-hidden" data-id="element-132" data-mp="w6mq1rl995aq">
                  <img src={builderAssets.fullLensSystem} alt="GritVue X1 with the Smoke, Amber and Clear lenses side by side" loading="lazy" className="absolute inset-x-0 top-1/2 w-full -translate-y-[52%]" data-id="element-133" data-mp="r93beqknaqi9" />
                </div>
                <figcaption className="grid grid-cols-3 border-t border-white/10" data-id="element-134" data-mp="370isqmc5izm6">
                  {(['smoke', 'amber', 'clear'] as const).map(id => {
                const option = lensOptions.find(lens => lens.id === id);
                const isPrimary = id === state.lens;
                const added = state.complete || state.fullLens;
                return <span key={id} className="flex flex-col items-center gap-0.5 border-l border-white/10 px-2 py-2.5 first:border-l-0" data-id="element-135" data-mp="2wt8m0srakdnw">
                        <span className="flex items-center gap-1.5 font-display text-[15px] font-extrabold uppercase tracking-wide text-offwhite" data-id="element-136" data-mp="36ptszjmvfyud">
                          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border border-white/40" style={{
                      backgroundColor: option?.swatch
                    }} data-id="element-137" data-mp="31pz0xcrd137q" />
                          {option?.name}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.16em] ${isPrimary ? 'text-steel-300' : added ? 'text-grit-500' : 'text-steel-500'}`} data-id="element-138" data-mp="1lebvtfjdfiey">
                          {isPrimary ? 'Your lens' : added ? '✓ Added' : '+ Add'}
                        </span>
                      </span>;
              })}
                </figcaption>
              </figure>
              <Decision builder={builder} kind="lensSystem" selected={state.fullLens} included={state.complete} addLabel="Add full lens system" declineLabel="No thanks" price={builderPrices.fullLensSystem} data-id="element-139" data-mp="39a9nbg0fzyh5" />
            </>}

          {step === 'case' && <>
              <h3 className={titleClass} data-id="element-140" data-mp="yihwpqhlm9h2">Protect your X1</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-141" data-mp="yizzl5yrnlxy">Toolbox, truck seat, jobsite floor. Keep it in one piece.</p>
              <div className="mt-5" data-id="element-142" data-mp="12hh73qm3myfw">
                <BuilderOptionCard title="Hard protective case" body="Semi-rigid zippered case sized for the X1 and its lenses." price={builderPrices.hardCase} selected={state.hardCase} included={state.complete} onToggle={() => builder.setHardCase(!state.hardCase)} media={<Thumb src={builderAssets.hardCase} alt="" data-id="element-144" data-mp="2u7rpy656qq3z" />} data-id="element-143" data-mp="2bh0ixb8o7bsr" />
              </div>
              <Decision builder={builder} kind="case" selected={state.hardCase} included={state.complete} addLabel="Add case" declineLabel="Skip" price={builderPrices.hardCase} data-id="element-145" data-mp="2q54ttt1hej0z" />
            </>}

          {step === 'care' && <>
              <h3 className={titleClass} data-id="element-146" data-mp="394itmrlw6nze">Keep your X1 clear</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-147" data-mp="392xueoomugv7">
                {state.complete ? 'Not part of the Complete System. Add it if you want it.' : 'Optional. Add it if you want it.'}
              </p>
              <div className="mt-5" data-id="element-148" data-mp="3lgulnxxn0bpv">
                <BuilderOptionCard title="GritVue anti-fog care kit" body="Mini anti-fog / lens cleaner and a microfiber cleaning cloth." price={builderPrices.careKit} selected={state.careKit} onToggle={() => builder.setCareKit(!state.careKit)} media={<Thumb src={builderAssets.careKit} alt="" data-id="element-150" data-mp="86ngdzj5frd4" />} data-id="element-149" data-mp="3qe38ibtqd3xg" />
              </div>
              <Decision builder={builder} kind="care" selected={state.careKit} included={false} addLabel="Add care kit" declineLabel="Skip" price={builderPrices.careKit} data-id="element-151" data-mp="czgngu23bbsd" />
            </>}

          {step === 'rx' && <>
              <div className="flex items-center gap-3" data-id="element-152" data-mp="db7heqpoigg9">
                <RxMark className="h-10 w-10 shrink-0" data-id="element-153" data-mp="d9mot6mxd7l1" />
                <h3 className={titleClass} data-id="element-154" data-mp="99m8fgg9mmbc">Need prescription vision?</h3>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-155" data-mp="n0r281wyckdr">
                The removable GritVue RX Insert fits behind the main X1 lens. Take the insert to your local optical
                professional and have your prescription lenses fitted into it. Then reinstall it in your X1.
              </p>
              <div className="mt-5" data-id="element-156" data-mp="3miidfp0ioh1q">
                <BuilderOptionCard title="RX capability" body="Removable · Optical-shop ready · Built for the X1. Prescription lenses are not included." price={builderPrices.rxInsert} selected={state.rx > 0} onToggle={() => builder.setRx(state.rx > 0 ? 0 : 1)} media={<Thumb src={builderAssets.rxInsert} alt="" data-id="element-158" data-mp="1aqv2q0eug40j" />} data-id="element-157" data-mp="4bb05a5mcjv8" />
              </div>
              <Decision builder={builder} kind="rx" selected={state.rx > 0} included={false} addLabel="Add RX" declineLabel="Skip" price={builderPrices.rxInsert} data-id="element-159" data-mp="2sngg1ndephbc" />
            </>}

          {step === 'protection' && <>
              <h3 className={titleClass} data-id="element-160" data-mp="3ce1uunvnklu8">Choose your coverage</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-161" data-mp="3gekn2j1gjtwy">
                Basic coverage is included. Extra protection is optional.{' '}
                <Link to="/legal/guarantee" className="font-semibold text-grit-500 underline-offset-2 hover:underline" data-id="element-162" data-mp="32me9n4pow117">
                  Terms apply
                </Link>
                .
              </p>
              <div role="radiogroup" aria-label="Protection" className="mt-5 grid gap-3" data-id="element-163" data-mp="3vaa4s4zm1kvv">
                {(state.complete ? completeProtection : essentialProtection).map(option => <BuilderOptionCard key={option.id} role="radio" title={option.name} body={option.body} price={option.price} selected={state.protection === option.id} onToggle={() => builder.setProtection(option.id)} data-id="element-164" data-mp="drusgq0y9keg" />)}
              </div>
              <div className="mt-5 flex" data-id="element-165" data-mp="3r18xm6azidi1">
                <button type="button" onClick={builder.nextStep} className="inline-flex flex-1 items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-166" data-mp="3m994awpf6iuf">
                  Review my X1
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-167" data-mp="1ism2mrr4i6kh" />
                </button>
              </div>
            </>}

          {step === 'review' && <>
              <h3 className={titleClass} data-id="element-168" data-mp="1rs94tfrofgel">Review your X1</h3>
              <p className="mt-2 text-[15px] text-steel-300" data-id="element-169" data-mp="1rqoa9q5d3xgq">Everything you chose, exactly as it ships.</p>
              <ul className="mt-5 grid gap-2 border-y border-white/10 py-4" aria-label="Your build" data-id="element-170" data-mp="1wphlfbx6fcdq">
                {lines.map(line => <li key={line.label} className="flex items-start justify-between gap-3 text-[15px]" data-id="element-171" data-mp="1447z0elvue2v">
                    <span className="flex items-start gap-2 text-offwhite" data-id="element-172" data-mp="19184anawrcsx">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-173" data-mp="1dthrhvkn1yri" />
                      {line.label}
                      {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                    </span>
                    <span className="shrink-0 tabular-nums text-steel-300" data-id="element-174" data-mp="lb428kddbl41">{money(line.price * line.quantity)}</span>
                  </li>)}
                <li className="flex items-start gap-2 text-[14px] text-steel-400" data-id="element-175" data-mp="35vxh3iq1mv4h">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-steel-500" aria-hidden="true" data-id="element-176" data-mp="3jvf6o0b5ycew" />
                  {protectionLabel(state)}
                </li>
              </ul>
              <div className="mt-4 flex items-end justify-between" data-id="element-177" data-mp="3eygg1rqpknlg">
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-178" data-mp="3tsbgqagpn6lk">Total</span>
                <span className="font-display text-5xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-179" data-mp="3ou063xilcs2a">
                  {money(total)}
                </span>
              </div>
              <p className="mt-2 flex items-center gap-2 text-[14px] font-semibold text-grit-500" data-id="element-180" data-mp="2g8eqn95e2ah">
                <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="gfhwvsgwz6h3" />
                Free U.S. shipping
              </p>
              <button type="button" onClick={builder.checkout} disabled={builder.routing} className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-182" data-mp="bhgaaxka4p0v">
                {builder.routing ? 'Opening cart' : 'Checkout'}
                {builder.routing ? <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-183" data-mp="2d9maeh3yt5t" /> : <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-184" data-mp="7b1ezapkujb2" />}
              </button>
              <div className="mt-3" data-id="element-185" data-mp="3piihhaxp4jm7">
                <NotConnected builder={builder} data-id="element-186" data-mp="3pjk2a74ikkh4" />
              </div>
              <UspsBadge note="Free U.S. shipping." className="mt-4" data-id="element-187" data-mp="3thrbmgwnejia" />
              <SecureCheckoutNote className="mt-4" data-id="element-188" data-mp="2jpicviitajrg" />
            </>}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-3 sm:px-6" data-id="element-189" data-mp="2opxrseiqrzbj">
        <button type="button" onClick={builder.prevStep} disabled={index === 0} className="inline-flex items-center gap-1.5 px-2 py-2 text-[12px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite disabled:invisible focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-190" data-mp="2aqy2r5rbhieu">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-191" data-mp="2tng3uds22unr" />
          Back
        </button>
      </div>
    </div>;
}